import { formatDateString } from "@/v2-common/utils/utils.date"
import Switch from "@/v2-ui/switch"
import { toast } from "@/v2-ui/toast"
import Badge from "@/v2-backoffice-client/components/badge"
import { useLicenseSeatGrantMutation } from "@/v2-backoffice-client/license/seats/license.seats.grant.mutation"
import { useLicenseSeatRevokeMutation } from "@/v2-backoffice-client/license/seats/license.seats.revoke.mutation"
import { evictLicenseSeatQueries } from "@/v2-backoffice-client/license/license.utils"
import LicenseSeatPersonLabel from "@/v2-backoffice-client/license/seats/LicenseSeatPersonLabel"
import { seatPersonNameOf } from "@/v2-backoffice-client/license/seats/license.seats.utils"
import type { LicenseSeatUsersQuery } from "@/v2-backoffice-client/license/seats/__types__/license.seats.query"

export type LicenseSeatHolderRowProps = {
  licenseId: number
  person: LicenseSeatUsersQuery["getLicenseSeatUsers"][number]
}

// One person on the seat panel, owning its own grant/revoke so a slow toggle
// never blocks the rest of the list. The switch is the whole action: on =
// holds a seat. A stale row (the person is no longer an active user of the
// customer) can only be switched off: the grant proc refuses such a person,
// and the row is listed exactly so the stale name can be revoked.
function LicenseSeatHolderRow(props: LicenseSeatHolderRowProps) {
  const { licenseId, person } = props

  const [grantSeat, grant] = useLicenseSeatGrantMutation({
    update: evictLicenseSeatQueries,
    onError(error) {
      toast({
        variant: "destructive",
        title: "Seat not granted",
        description: error.message,
      })
    },
  })
  const [revokeSeat, revoke] = useLicenseSeatRevokeMutation({
    update: evictLicenseSeatQueries,
    onError(error) {
      toast({
        variant: "destructive",
        title: "Seat not revoked",
        description: error.message,
      })
    },
  })

  const isSaving = grant.loading || revoke.loading
  const granted = person.SeatGrantedAt
    ? `Seat since ${formatDateString(person.SeatGrantedAt)}${person.SeatGrantedByUserName ? ` · by ${person.SeatGrantedByUserName}` : ""}`
    : null

  return (
    <li className="flex items-center justify-between gap-4 py-2">
      <div className="flex min-w-0 items-center gap-2">
        <LicenseSeatPersonLabel person={person} detail={granted} />
        {!person.IsActiveUser && (
          <Badge
            size="sm"
            variant="warning"
            tooltip="No longer an active user of this customer (left, blocked or deleted). The seat grants nothing but still counts as a named person, so the license does not fall back to covering everyone. Switch it off to revoke."
          >
            stale
          </Badge>
        )}
      </div>
      <Switch
        aria-label={`${person.HasSeat ? "Revoke" : "Grant"} seat for ${seatPersonNameOf(person)}`}
        data-feature-code="UI_BackofficeLicense_Seats_Toggle"
        checked={person.HasSeat}
        isLoading={isSaving}
        onCheckedChange={(checked) => {
          const variables = { licenseId, userId: person.UserId }
          if (checked) {
            void grantSeat({ variables })
          } else {
            void revokeSeat({ variables })
          }
        }}
      />
    </li>
  )
}

export default LicenseSeatHolderRow
